/// <amd-module name="Widgets/Burndown/BurndownWidgetComponents/AsyncDataHelpers" />
import { ProcessesQuery } from "Analytics/Boards/BoardsQueries/ProcessesQuery";
import { WorkItemTypeFieldsQuery } from "Analytics/Boards/BurndownQueries/WorkItemTypeFieldsQuery";
import { ICacheableQueryService, IDashboardsCacheableQueryServiceName } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import { CacheableQueryService } from "Reporting/Common/CacheableQueryService";
import { getProject } from "Tfs/Widget/Context";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { ModefulValueSetting } from "WidgetCommon/ModernWidgetTypes/ModefulValueSetting";
import * as Resources from "Widgets/Burndown/Resources";
import { WorkItemTypeColorAndIcon } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTypeIconMetadataService, WorkItemTypeIconMetadataServiceId } from "Wit/UI/Services/WorkItemTypeIconMetadataService";
import { WorkItemTypeFilterMode } from "./BurndownDataContract";
import { WorkItemFieldDescriptor } from "./BurndownDataContract";

export interface LegacyProjectInformation {
    ProjectUsesKnownBugCategoryName: boolean;
}

export class AsyncDataHelpers {
    public static WitRequirementsCategory = "Microsoft.RequirementCategory";

    /**
     * Retrieves the color dictionary for the given work item types as defined by the given project.
     * @param workItemTypes A list of work item types for which to retrieve the colors
     * @param projectName The name of the project which defines colors
     */
    public static async getWitColorDictionary(
        pageContext: IVssPageContext,
        workItemTypes: string[],
        projectName: string
    ): Promise<{ [key: string]: string }> {
        const colorService: IWorkItemTypeIconMetadataService = pageContext.getService<IWorkItemTypeIconMetadataService>(
            WorkItemTypeIconMetadataServiceId
        );
        const colorDictionary: { [key: string]: string } = {};
        for (const workItemTypeName of workItemTypes) {
            const colorInfo: WorkItemTypeColorAndIcon = await colorService.getWorkItemIcon(projectName, workItemTypeName);
            colorDictionary[workItemTypeName] = `#${colorInfo.color.substr(-6)}`;
        }
        return colorDictionary;
    }

    // is work item a backlog or work item type
    public static async getWorkItemTypes(
        pageContext: IVssPageContext,
        workItemTypeFilter: ModefulValueSetting<string, string>,
        includeBugsForRequirementCategory: boolean,
        isAutomationScenario: boolean
    ): Promise<string[]> {
        if (workItemTypeFilter.identifier == WorkItemTypeFilterMode[WorkItemTypeFilterMode.WorkItemType]) {
            return Promise.resolve([workItemTypeFilter.settings]);
        } else {
            // In the backlog case we use the current context project to look up the work item types associated with a backlog category.
            const project = getProject(pageContext);
            return AsyncDataHelpers.getProjectWorkItemTypesOfBacklogCategory(
                pageContext,
                project.id,
                workItemTypeFilter.settings,
                includeBugsForRequirementCategory,
                isAutomationScenario
            );
        }
    }

    public static async getWorkItemTypesForTeam(
        pageContext: IVssPageContext,
        workItemTypeFilter: ModefulValueSetting<string, string>,
        teamId: string,
        isAutomationScenario: boolean
    ): Promise<string[]> {
        if (workItemTypeFilter.identifier == WorkItemTypeFilterMode[WorkItemTypeFilterMode.WorkItemType]) {
            return Promise.resolve([workItemTypeFilter.settings]);
        } else {
            // In the backlog case we use the current context project to look up the work item types associated with a backlog category.
            const project = getProject(pageContext);
            return AsyncDataHelpers.getProjectWorkItemTypesOfBacklogCategoryForTeam(
                pageContext,
                project.id,
                workItemTypeFilter.settings,
                teamId,
                isAutomationScenario
            );
        }
    }

    public static async getWorkItemTypeFieldsPromise(
        pageContext: IVssPageContext,
        workItemTypes: string[],
        projectIds: string[],
        dataService: ICacheableQueryService,
        isAutomationScenario: boolean,
        commandName?: string
    ): Promise<WorkItemFieldDescriptor[]> {
        const workItemTypeFieldsQuery = new WorkItemTypeFieldsQuery(pageContext, projectIds, workItemTypes, commandName).setAutomationMode(
            isAutomationScenario
        );
        return dataService.getCacheableQueryResult(workItemTypeFieldsQuery);
    }

    /**
     * Retrieves the work item types associated with the given backlog category, at context of a project.
     * Includes work items from the bugs category if the backlog passed in is the requirements backlog, and the
     * includeBugsForRequirementCategory argument is true.
     * @param projectId The project ID to use when looking up the process configuration
     * @param backlogCategory The backlog category for which to look up the associated work item types
     * @param includeBugsForRequirementCategory Defaults to false. Set to true if adding bugs category work item types to the requirements category work item types is desired.
     */
    public static async getProjectWorkItemTypesOfBacklogCategory(
        pageContext: IVssPageContext,
        projectId: string,
        backlogCategory: string,
        includeBugsForRequirementCategory?: boolean,
        isAutomationScenario?: boolean
    ): Promise<string[]> {
        // Get team Process, and then select the WIT's associated with the requested backlog Category.
        // Include items of the "bugs" Category if "includeBugsForRequirementCategory" is true
        // Filters out duplicates, since the query is project-scoped but each team can have their own configuration
        // NOTE: WIT's can be associated with the backlog and marked as bug types. The "Basic" process "Issue" is an OOB example of this. These WIT's should be included regardless of includeBugsForRequirementCategory.
        const cacheableQueryService = pageContext.getService<ICacheableQueryService>(IDashboardsCacheableQueryServiceName);
        return cacheableQueryService
            .getCacheableQueryResult(ProcessesQuery.onProject(pageContext, projectId).setAutomationMode(isAutomationScenario))
            .then(types => {
                const result: string[] = [];
                for (const entry of types) {
                    const isOfBacklogCategory = entry.BacklogCategoryReferenceName === backlogCategory;
                    const includeAsABug =
                        entry.IsBugType && includeBugsForRequirementCategory && backlogCategory === AsyncDataHelpers.WitRequirementsCategory;
                    if ((isOfBacklogCategory || includeAsABug) && result.indexOf(entry.WorkItemType) < 0) {
                        result.push(entry.WorkItemType);
                    }
                }
                return result;
            });
    }

    /**
     * Retrieves the work item types associated with the given backlog category, at context of a project and team.
     * Includes work items from the bugs category based on the team settings that describe which category the bugs should be included
     * @param projectId The project ID to use when looking up the process configuration
     * @param backlogCategory The backlog category for which to look up the associated work item types
     * @param teamId The team ID to use when looking up the process configuration
     */
    public static async getProjectWorkItemTypesOfBacklogCategoryForTeam(
        pageContext: IVssPageContext,
        projectId: string,
        backlogCategory: string,
        teamId: string,
        isAutomationScenario: boolean
    ): Promise<string[]> {
        // Get team Process, and then select the WIT's associated with the requested backlog Category.
        // Include items of the "bugs" Category if the team settings has described it to be part of a backlog
        // This has three option settings :1) Include bugs in requirement 2) Include bugs in task 3) Bugs not managed in backlogs and boards
        const cacheableQueryService = pageContext.getService<ICacheableQueryService>(IDashboardsCacheableQueryServiceName);
        return cacheableQueryService
            .getCacheableQueryResult(ProcessesQuery.onTeam(pageContext, projectId, teamId).setAutomationMode(isAutomationScenario))
            .then(types => {
                const result: string[] = [];
                for (const entry of types) {
                    const isOfBacklogCategory = entry.BacklogCategoryReferenceName === backlogCategory;
                    if (isOfBacklogCategory) {
                        result.push(entry.WorkItemType);
                    }
                }
                return result;
            });
    }

    public static async getLegacyProjectData(pageContext: IVssPageContext): Promise<LegacyProjectInformation> {
        const defaultLegacyProjectInformation = { ProjectUsesKnownBugCategoryName: false };
        const contributionService: IVssContributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        const result: { value: LegacyProjectInformation } | undefined = await contributionService.getDataAsync(
            "ms.vss-dashboards-web.legacy-project-data-provider"
        );
        return result ? result.value : defaultLegacyProjectInformation;
    }

    /**
     * Retrieves the plural name of the given backlog category.
     * @param projectId The project ID to use when looking up the process configuration
     * @param backlogCategory The backlog category for which to retrieve the plural name
     */
    public static async getPluralBacklogNameOfBacklogCategory(
        pageContext: IVssPageContext,
        projectId: string,
        backlogCategory: string,
        isAutomationScenario?: boolean
    ): Promise<string> {
        const getMatchFailure = (backlogCategory: string) => {
            return Promise.reject<string>(`Failed to find the backlog category '${backlogCategory}' in the process configuration.`);
        };
        const cacheableQueryService = pageContext.getService<CacheableQueryService>(IDashboardsCacheableQueryServiceName);
        try {
            return await cacheableQueryService
                .getCacheableQueryResult(ProcessesQuery.onProject(pageContext, projectId).setAutomationMode(isAutomationScenario))
                .then(types => {
                    const index = types.findIndex(entry => {
                        return entry.BacklogCategoryReferenceName === backlogCategory;
                    });
                    const result = index != -1 ? types[index] : null;
                    return result != null ? result.BacklogName : getMatchFailure(backlogCategory);
                });
        } catch (error) {
            return Promise.reject(format(Resources.ModernWidget_PluralBacklogNameErrorFormat, error));
        }
    }
}
